'use client';

import { InfoIcon } from 'assets/icons';
import { useModal } from 'components/index';
import { OffersModalNewContext } from 'components/shared/OffersModal';
import { forwardRef, useContext } from 'react';
import styles from './SelectionSection.module.css';

interface SelectionOption {
  label: string;
  value: string;
  description?: string;
  icon?: React.ReactNode;
}

interface SelectionSectionProps {
  title: string;
  subtitle?: string;
  options: SelectionOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  disabledOptions?: string[];
  showInfoButton?: boolean;
  infoModal?: React.ReactNode;
  infoModalSecond?: React.ReactNode;
  dataButtonType?: string;
  dataProduct?: string;
  variant?: 'default' | 'card';
  showRequiredHint?: boolean;
  requiredHintText?: string;
  onDisabledOptionClick?: () => void;
}

const SelectionSection = forwardRef<HTMLElement, SelectionSectionProps>(
  (
    {
      title,
      subtitle,
      options,
      selectedValue,
      onSelect,
      disabledOptions = [],
      showInfoButton = false,
      infoModal,
      infoModalSecond,
      dataButtonType,
      dataProduct,
      variant = 'default',
      showRequiredHint = false,
      requiredHintText = 'Select an option to continue',
      onDisabledOptionClick,
    },
    ref,
  ) => {
    const ModalClient = useModal();
    const OffersModalNewClient = useContext(OffersModalNewContext);

    const openInfoModal = () => {
      if (infoModalSecond && OffersModalNewClient) {
        OffersModalNewClient.set(infoModalSecond);
        OffersModalNewClient.show();
      } else if (infoModal && ModalClient) {
        ModalClient.set(infoModal);
        ModalClient.show();
      }
    };

    return (
      <section className={styles.selectionSectionWrapper} ref={ref}>
        <div className={styles.sectionRowTitle}>
          <h5>
            {title} {subtitle && <span className="subtitle">{subtitle}</span>}
          </h5>
          {showInfoButton && (OffersModalNewClient || ModalClient) && (
            <button onClick={openInfoModal}>
              <InfoIcon
                style={{
                  width: '1.6rem',
                  height: 'auto',
                  position: 'relative',
                  bottom: '1px',
                }}
                fill="#000"
              />
            </button>
          )}
        </div>
        <div className={styles.selectionOptions} data-variant={variant}>
          <div
            className={styles.optionsContainer}
            role="radiogroup"
            aria-label={title}
          >
            {options.map((option) => {
              const isDisabled = disabledOptions.includes(option.value);
              const isActionableWhileDisabled =
                isDisabled && !!onDisabledOptionClick;

              return (
              <button
                key={option.value}
                type="button"
                className={[
                  selectedValue === option.value ? styles.active : '',
                  isDisabled ? styles.isDisabled : '',
                  isActionableWhileDisabled ? styles.isActionable : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-buttontype={dataButtonType}
                data-product={dataProduct}
                disabled={isDisabled && !isActionableWhileDisabled}
                aria-disabled={isDisabled}
                onClick={() => {
                  if (isDisabled) {
                    onDisabledOptionClick?.();
                    return;
                  }
                  onSelect(option.value);
                }}
                role="radio"
                aria-checked={selectedValue === option.value}
              >
                {option.icon ? (
                  <span className={styles.icon}>{option.icon}</span>
                ) : null}
                <div className={styles.text}>
                  <div className={styles.title}>{option.label}</div>
                  {option.description ? (
                    <p className={styles.description}>{option.description}</p>
                  ) : null}
                </div>
              </button>
            );
            })}
          </div>
          {showRequiredHint ? (
            <p className={styles.requiredHint} role="alert">
              {requiredHintText}
            </p>
          ) : null}
        </div>
      </section>
    );
  },
);

SelectionSection.displayName = 'SelectionSection';

export default SelectionSection;
